import { computed } from 'vue'

/**
 * Chat audio orchestrator.
 *
 * Three playback paths:
 *   - playMessage(senderId): short "ding" when a new message arrives.
 *     Debounced per-sender (default 3s) so ten rapid messages from the same
 *     user don't trigger ten dings.
 *   - startCallRingtone(): loops the call-ringtone until stopCallRingtone().
 *     Used by <ChatIncomingCallDialog>.
 *   - play(url): raw playback, for the "Test" button in settings.
 *
 * Respects the user's preferences on `chatProfile`:
 *   - messageSoundEnabled / callSoundEnabled (toggles)
 *   - soundVolume (0..100)
 *   - messageSoundId / callSoundId (built-in id OR `custom:<uuid>`)
 */

// Matches server/utils/chatSounds.ts. Duplicated so the client doesn't need a
// round-trip just to render the picker — the list is stable and tiny.
export const MESSAGE_SOUND_PRESETS = [
    { id: 'default', name: 'Default Ding', url: '/sounds/message-default.mp3' },
    { id: 'pop',     name: 'Pop',          url: '/sounds/message-pop.mp3' },
    { id: 'chime',   name: 'Chime',        url: '/sounds/message-chime.mp3' },
] as const

export const CALL_SOUND_PRESETS = [
    { id: 'default', name: 'Classic Ring', url: '/sounds/call-default.mp3' },
    { id: 'soft',    name: 'Soft Tone',    url: '/sounds/call-soft.mp3' },
    { id: 'marimba', name: 'Marimba',      url: '/sounds/call-marimba.mp3' },
] as const

const SENDER_DEBOUNCE_MS = 3000

// Module-level state — singleton across composable calls.
const recentSenders = new Map<string, number>()
let ringtoneAudio: HTMLAudioElement | null = null

function resolveSoundUrl(soundId: string, kind: 'message' | 'call', customSounds: Array<{ id: string; s3Key: string }>): string | null {
    if (!soundId) return null
    if (soundId.startsWith('custom:')) {
        const customId = soundId.slice('custom:'.length)
        const hit = customSounds.find(s => s.id === customId)
        if (!hit) return null
        // Route through the signed-URL proxy so private buckets work the same
        // as public ones, and the URL stays short.
        return `/api/assets/serve?key=${encodeURIComponent(hit.s3Key)}`
    }
    const presets = kind === 'message' ? MESSAGE_SOUND_PRESETS : CALL_SOUND_PRESETS
    return presets.find(p => p.id === soundId)?.url || null
}

export function useChatAudio() {
    const { user } = useUserSession()

    const profile = computed(() => (user.value as any)?.chatProfile || {})
    const volume = computed(() => Math.max(0, Math.min(1, (profile.value.soundVolume ?? 70) / 100)))
    const messageEnabled = computed(() => profile.value.messageSoundEnabled !== false)
    const callEnabled    = computed(() => profile.value.callSoundEnabled !== false)
    const customSounds   = computed(() => profile.value.customSounds || [])

    async function play(url: string, { loop = false, volumeOverride }: { loop?: boolean, volumeOverride?: number } = {}): Promise<HTMLAudioElement | null> {
        if (typeof window === 'undefined') return null
        try {
            const audio = new Audio(url)
            audio.volume = volumeOverride ?? volume.value
            audio.loop = loop
            // `error` fires for 404, decode errors, bad MIME, etc. — attach
            // this BEFORE play() so a missing file is visible in DevTools
            // rather than looking like a bug in the trigger path.
            audio.addEventListener('error', () => {
                const mediaErr = audio.error
                console.warn(`[chat-audio] failed to load ${url}`, {
                    code: mediaErr?.code,
                    message: mediaErr?.message
                })
            }, { once: true })
            // Route to the user's preferred output device when set + supported.
            // Fire-and-forget — setSinkId() returns a promise we don't need
            // to await before play(); if it rejects (revoked permission,
            // device unplugged) we just play on the default sink.
            const outputDeviceId: string = (profile.value.audioOutputDeviceId || '') as string
            if (outputDeviceId && typeof (audio as any).setSinkId === 'function') {
                (audio as any).setSinkId(outputDeviceId).catch((err: unknown) => {
                    console.warn('[chat-audio] setSinkId failed; using default sink', err)
                })
            }
            try {
                await audio.play()
            } catch (err: any) {
                // `NotAllowedError` is the browser's autoplay gate — next
                // user gesture unlocks. `NotSupportedError` / `AbortError`
                // are raised for load failures that the 'error' event above
                // already logged, so we skip them here to avoid double
                // warnings. Anything else is genuinely unexpected.
                const name = err?.name
                if (name === 'NotAllowedError') {
                    console.debug('[chat-audio] autoplay blocked, next click will unlock', url)
                } else if (name !== 'NotSupportedError' && name !== 'AbortError') {
                    console.warn('[chat-audio] play() rejected for', url, err)
                }
            }
            return audio
        } catch (err: any) {
            console.warn('[chat-audio] unable to construct Audio for', url, err)
            return null
        }
    }

    /**
     * Play the user's message-received sound, unless:
     *  - sounds disabled
     *  - the same sender played a sound within SENDER_DEBOUNCE_MS
     */
    async function playMessage(senderId?: string | null): Promise<void> {
        if (!messageEnabled.value || volume.value === 0) return
        const now = Date.now()
        if (senderId) {
            const last = recentSenders.get(senderId) || 0
            if (now - last < SENDER_DEBOUNCE_MS) return
            recentSenders.set(senderId, now)
            // Cheap GC of stale entries (keep map bounded)
            if (recentSenders.size > 200) {
                for (const [k, t] of recentSenders) {
                    if (now - t > SENDER_DEBOUNCE_MS * 4) recentSenders.delete(k)
                }
            }
        }
        const soundId = profile.value.messageSoundId || 'default'
        const url = resolveSoundUrl(soundId, 'message', customSounds.value)
        if (!url) {
            console.warn(`[chat-audio] no URL for message sound id "${soundId}" — preset missing or custom sound removed`)
            return
        }
        await play(url)
    }

    /**
     * Start looping the ringtone. Safe to call repeatedly — subsequent calls
     * are no-ops while already ringing.
     */
    async function startCallRingtone(): Promise<void> {
        if (!callEnabled.value || volume.value === 0) return
        if (ringtoneAudio) return
        const soundId = profile.value.callSoundId || 'default'
        const url = resolveSoundUrl(soundId, 'call', customSounds.value)
        if (!url) {
            console.warn(`[chat-audio] no URL for call sound id "${soundId}" — preset missing or custom sound removed`)
            return
        }
        ringtoneAudio = await play(url, { loop: true })
    }

    function stopCallRingtone(): void {
        if (!ringtoneAudio) return
        try { ringtoneAudio.pause(); ringtoneAudio.currentTime = 0 } catch { /* noop */ }
        ringtoneAudio = null
    }

    /**
     * Play any sound once, ignoring debounce/toggles — used by the settings
     * dialog's "Test" buttons.
     */
    async function test(soundId: string, kind: 'message' | 'call'): Promise<void> {
        const url = resolveSoundUrl(soundId, kind, customSounds.value)
        if (!url) {
            console.warn(`[chat-audio] no URL for ${kind} sound id "${soundId}" — test cannot play`)
            return
        }
        await play(url, { volumeOverride: volume.value })
    }

    return {
        playMessage,
        startCallRingtone,
        stopCallRingtone,
        test,
        volume,
        messageEnabled,
        callEnabled,
        customSounds,
    }
}
